"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "./button"

const Calendar = ({ onSelect, mode = "single", selected, onClose }) => {
  const [currentDate, setCurrentDate] = React.useState(new Date())
  const [selectedRange, setSelectedRange] = React.useState({
    from: selected?.from || null,
    to: selected?.to || null
  })

  const calendarRef = React.useRef(null)

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [onClose])

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate()
  
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay()

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    )
  }

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    )
  }

  const handleDateClick = (day) => {
    const clickedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    )

    if (mode === "range") {
      // Handle range selection
      if (!selectedRange.from || (selectedRange.from && selectedRange.to)) {
        // First selection or reset selection
        setSelectedRange({ from: clickedDate, to: null })
      } else if (clickedDate >= selectedRange.from) {
        // Complete the range
        const newRange = { from: selectedRange.from, to: clickedDate }
        setSelectedRange(newRange)
        if (onSelect) onSelect(newRange)
      } else {
        // Start a new range if clicked date is before the from date
        setSelectedRange({ from: clickedDate, to: null })
      }
    } else {
      // Handle single date selection
      if (onSelect) onSelect(clickedDate)
    }
  }

  const isInRange = (day) => {
    if (mode !== "range" || !selectedRange.from) return false
    
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    )
    
    if (selectedRange.to) {
      return date >= selectedRange.from && date <= selectedRange.to
    } else {
      return date.getTime() === selectedRange.from.getTime()
    }
  }

  const isRangeStart = (day) => {
    if (!selectedRange.from) return false
    
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    )
    
    return date.getTime() === selectedRange.from.getTime()
  }

  const isRangeEnd = (day) => {
    if (!selectedRange.to) return false
    
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    )
    
    return date.getTime() === selectedRange.to.getTime()
  }

  return (
    <div
      ref={calendarRef}
      className="p-4 bg-white border border-gray-200 rounded-lg shadow-lg w-[447px] z-50"
    >
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={handlePrevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-normal">
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <Button variant="ghost" onClick={handleNextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDayOfMonth }, (_, i) => (
          <div key={`empty-${i}`} className="h-10" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1
          const date = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day
          )
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          
          const isToday = date.getTime() === today.getTime()
          const inRange = isInRange(day)
          const rangeStart = isRangeStart(day)
          const rangeEnd = isRangeEnd(day)

          return (
            <Button
              key={day}
              variant="ghost"
              className={`h-10 rounded-full relative
                ${rangeStart ? "bg-blue-600 text-white rounded-r-none" : ""}
                ${rangeEnd ? "bg-blue-600 text-white rounded-l-none" : ""}
                ${inRange && !rangeStart && !rangeEnd ? "bg-blue-100 text-blue-900 rounded-none" : ""}
                ${isToday && !inRange ? "border border-blue-500" : ""}
                ${!inRange ? "text-gray-700 hover:bg-gray-100" : ""}
              `}
              onClick={() => handleDateClick(day)}
            >
              {day}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

Calendar.displayName = "Calendar"

export { Calendar }